// ─────────────────────────────────────────────────────────────────
// Annonsens bilder och deras frilagda varianter. Den bild som visas står
// alltid i imageUrl/galleryUrls (så marknadsplats, sök och kort fungerar
// oförändrat); imageVariants minns originalet så valet kan ångras.
// Annonser utan imageVariants (alla från före funktionen) påverkas inte.
// ─────────────────────────────────────────────────────────────────

export interface ImageVariant {
  originalUrl: string;
  cutoutUrl: string;
  useCutout: boolean;
}

export interface ListingImages {
  imageUrl: string;
  galleryUrls: string[];
  imageVariants: ImageVariant[];
}

const UPLOAD_PATH_RE = /\/uploads\/images\/([A-Za-z0-9._-]+)$/;

/** Relativ sökväg för en uppladdad bild, oavsett om URL:en är absolut (prod) eller relativ (dev). */
export function uploadPath(url: string | null | undefined): string | null {
  const m = url?.split(/[?#]/)[0].match(UPLOAD_PATH_RE);
  return m ? `/uploads/images/${m[1]}` : null;
}

export function sameImage(a: string | null | undefined, b: string | null | undefined): boolean {
  const pa = uploadPath(a);
  return pa !== null && pa === uploadPath(b);
}

/** Bara säljarens egna uppladdade foton kan friläggas (inte externa URL:er eller AI-bilder). */
export function canCutout(url: string | null | undefined): boolean {
  const p = uploadPath(url);
  return p !== null && !/\/uploads\/images\/ai-/.test(p);
}

/** Varianten som bilden ingår i — som original eller som frilagd version. */
export function findVariant(images: ListingImages, url: string): { variant: ImageVariant; showingCutout: boolean } | null {
  for (const variant of images.imageVariants) {
    if (sameImage(variant.cutoutUrl, url)) return { variant, showingCutout: true };
    if (sameImage(variant.originalUrl, url)) return { variant, showingCutout: false };
  }
  return null;
}

function swap(images: ListingImages, from: string, to: string): Pick<ListingImages, 'imageUrl' | 'galleryUrls'> {
  return {
    imageUrl: sameImage(images.imageUrl, from) ? to : images.imageUrl,
    galleryUrls: images.galleryUrls.map(u => (sameImage(u, from) ? to : u)),
  };
}

function upsert(variants: ImageVariant[], next: ImageVariant): ImageVariant[] {
  const rest = variants.filter(v => !sameImage(v.originalUrl, next.originalUrl));
  return [...rest, next];
}

/** "Använd frilagd": byt originalet mot den frilagda bilden där den visas. */
export function chooseCutout(images: ListingImages, originalUrl: string, cutoutUrl: string): ListingImages {
  return {
    ...swap(images, originalUrl, cutoutUrl),
    imageVariants: upsert(images.imageVariants, { originalUrl, cutoutUrl, useCutout: true }),
  };
}

/** "Visa original": byt tillbaka till originalet. Den frilagda filen finns kvar. */
export function chooseOriginal(images: ListingImages, cutoutUrl: string): ListingImages {
  const found = findVariant(images, cutoutUrl);
  if (!found?.showingCutout) return images;
  const { originalUrl } = found.variant;
  return {
    ...swap(images, cutoutUrl, originalUrl),
    imageVariants: upsert(images.imageVariants, { ...found.variant, useCutout: false }),
  };
}

/** Varianter vars bild inte längre finns i annonsen tas bort innan sparning. */
export function pruneVariants(images: ListingImages): ImageVariant[] {
  const shown = [images.imageUrl, ...images.galleryUrls];
  return images.imageVariants.filter(v => shown.some(u => sameImage(u, v.cutoutUrl) || sameImage(u, v.originalUrl)));
}
